import React, { createContext, ReactNode, useContext } from 'react';
import {
	useMutation,
	UseMutationResult,
	useQuery,
	useQueryClient,
	UseQueryResult,
} from '@tanstack/react-query';
import { useStore } from '../../../utils/hooks';
import { V2 } from '@icecreamsocial/api-resources';
import { ClientBusiness, queryKeys } from '../../../queries/businesses';
import { queryKeys as campaignsQueryKeys } from '../../../queries/campaigns';
import { MarketSegment } from '../../containers/SegmentSelector/types';
import { useMarketSegments } from '../../../utils/hooks/useMarketSegments';

type SegmentationOption = {
	value: string;
	label: string;
}

type UpdateBusinessParams = Exclude<ClientBusiness, 'marketVertical' | 'marketSubCategory' | 'emailAddress' | 'allowedDomains' | 'createdAt' | 'updatedAt'> & {
	marketVertical: SegmentationOption;
	marketSubCategory: SegmentationOption;
}

type BusinessContext = {
	business: UseQueryResult<ClientBusiness>;
	update: UseMutationResult<ClientBusiness>;
	bulkUpdate: UseMutationResult<ClientBusiness>;
};

const REQUIRED_PROFILE = ['name', 'businessAddress', 'supportEmail', 'currency', 'language', 'marketVertical'];
const REQUIRED_ECOMMERCE = ['webstoreURL', 'eCommerceSolution'];
const REQUIRED_BRAND = ['logo'];
const REQUIRED_POLICIES = ['termsAndConditionsURL', 'privacyPolicyURL'];

const BusinessCtx = createContext<BusinessContext | null>(null);

export const useBusiness = () => {
	return useContext(BusinessCtx);
}

/**
 * Makes the campaign data and loading state available to all child components
 */
export const BusinessProvider = ({
	children,
	clientId,
}: {
	clientId: string;
	children: ReactNode;
}) => {
	const { Orchestrators, __displayFlowSuccess } = useStore();
	const headers = { Authorization: Orchestrators.Auth.authHeader };
	const queryClient = useQueryClient();
	const { getSegmentById: getVerticalById } = useMarketSegments('Vertical');
	const { getSegmentById: getSubCategoryById
	} = useMarketSegments('SubCategory');

	const business = useQuery<ClientBusiness>(
		queryKeys.getBusiness({ clientId }),
		async () => {
			const { data } = await V2.Company.getCompanyInformation(
				{ clientId },
				{},
				headers
			);

			return data?.data?.company;
		},
		{ enabled: !!clientId, refetchOnWindowFocus: false, refetchOnMount: false }
	);

	const update = useMutation<ClientBusiness, unknown, UpdateBusinessParams>(
		async (values) => {
			const { createdAt, updatedAt, emailAddress, allowedDomains, ...currentBusiness } = business?.data;
			const isFile = (check) => typeof check?.name === 'string';

			// Merge values into current business but do not overwrite with empty strings/null
			const merged: any = { ...currentBusiness };
			for (const k in values) {
				const v = (values as any)[k];
				if (v === '' || v == null) continue;
				merged[k] = v;
			}
			const { logo, ...cleaned } = merged;

			// Compute segmentation values and avoid sending empty strings
			const rawMarketSub = (cleaned as any).marketSubCategory ?? (cleaned as any).marketSubcategory;
			const marketVerticalValue = (cleaned as any).marketVertical ? getSegmentationValue((cleaned as any).marketVertical) : '';
			const marketSubCategoryValue = rawMarketSub ? getSegmentationValue(rawMarketSub) : '';

			if (marketSubCategoryValue === '') {
				delete cleaned.marketSubCategory;
			}

			const formData = new FormData();

			for (let key in cleaned) {
				if (key === 'marketVertical' || key === 'marketSubCategory') continue;
				formData.set(key, cleaned[key]);
			}

			// debug: show merged state and computed isConfigured
			// TEMP LOG — remove after debugging
			// eslint-disable-next-line no-console
			// console.log('DBG isConfigured (provider):', {
			// 	merged,
			// 	cleaned,
			// 	isConfigured: isConfigured(merged).all,
			// 	marketVerticalValue,
			// 	marketSubCategoryValue,
			// });
			formData.set('isConfigured', `${isConfigured(merged).all}`);

			if (marketVerticalValue) {
				formData.set('marketVertical', marketVerticalValue);
			}

			if (marketSubCategoryValue) {
				formData.set('marketSubCategory', marketSubCategoryValue);
			}

			// set images only if they are files.  if they are strings, they are most likely the previous uri
			// this is so that we don't overwrite the image on subsequent saves
			if (isFile(logo)) {
				formData.set('logo', logo);
			}

			const { data } = await V2.Company.updateCompanyInformation(
				{
					clientId,
					formData
				},
				{},
				headers
			);

			return data?.data?.company;
		},
		{
			onSuccess: (data: ClientBusiness) => {
				queryClient.setQueryData<Partial<ClientBusiness>>(
					queryKeys.getBusiness({ clientId }),
					(prev = {}) => {
						const updated = {
							...prev,
							...data,
							marketVertical: getVerticalById(`${data.marketVertical}`),
							marketSubCategory: getSubCategoryById(`${data.marketSubCategory}`),
						}
						return updated;
					});
				/**
				 * @todo eliminate store dependency
				 */
				__displayFlowSuccess('Business settings updated!');
			},
		}
	);

	const bulkUpdate = useMutation<ClientBusiness, unknown, UpdateBusinessParams>(
		async (values) => {
			await update.mutateAsync(values);
			const { data } = await V2.Company.bulkUpdateCompanyCampaigns({
				clientId,
				...values
			}, {}, headers);
			return data.data;
		},
		{
			onSuccess: () => {
				__displayFlowSuccess('Business and Campaign Settings updated!');
				// the bulk call modifies all campaigns that belong to the client so we make sure to invalidate the cache for the campaigns call so that they are refreshed
				queryClient.invalidateQueries(campaignsQueryKeys.getCampaigns._def);
			}
		}
	)

	const context = {
		business,
		update,
		bulkUpdate
	};

	return (
		<BusinessCtx.Provider value={context}>{children}</BusinessCtx.Provider>
	);
};

function isConfigured(business: Partial<ClientBusiness>): Record<'profile' | 'eCommerce' | 'brand' | 'policies' | 'all', boolean> {
	const getField = (obj: any, key: string) => {
		if (key === 'marketSubCategory') return obj?.marketSubCategory ?? obj?.marketSubcategory;
		return obj?.[key];
	};
	// const profile = REQUIRED_PROFILE.reduce((complete, key) => !!business[key] && complete, true);
	const profile = REQUIRED_PROFILE.reduce((complete, key) => !!getField(business as any, key) && complete, true);
	const eCommerce = REQUIRED_ECOMMERCE.reduce((complete, key) => !!getField(business as any, key) && complete, true);
	const brand = REQUIRED_BRAND.reduce((complete, key) => !!getField(business as any, key) && complete, true);
	const policies = REQUIRED_POLICIES.reduce((complete, key) => !!getField(business as any, key) && complete, true);

	const status = {
		all: profile && eCommerce && brand && policies,
		profile,
		eCommerce,
		brand,
		policies,
	};

	return status;
}

function getSegmentationValue(segment: SegmentationOption | MarketSegment): string {
	if (segment.value) return `${segment.value}`;

	if ('id' in segment && segment.id) return `${segment.id}`;

	return '';
}